import { useState } from 'react'
import type { RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  bulkAssignProcurement,
  type AssignConflict,
  type BulkAssignResult,
} from '../../../shared/api/requestItems.api'
import { toast } from '../../../shared/ui/toastStore'
import { confirm } from '../../../shared/ui/confirmStore'

/** Текст подтверждения: у позиций уже есть живая работа других закупщиков. */
export function conflictsMessage(conflicts: AssignConflict[]): string {
  const list = conflicts
    .slice(0, 5)
    .map((c) => `${c.partNumber} — запрос ${c.fileCode} (${c.supplierName}) от ${c.byName}`)
    .join('\n')
  const tail = conflicts.length > 5 ? `\n…и ещё ${conflicts.length - 5} поз.` : ''
  return `${list}${tail}\n\nНазначение скроет эти позиции из очереди у тех, кто уже отправил по ним запросы (их запросы и квоты сохранятся). Всё равно назначить?`
}

/**
 * Назначить позиции через bulk-assign; если у других закупщиков по ним идёт
 * живая работа — спросить и повторить с force. `null` — пользователь отказался.
 * Общий шаг для массового назначения и выпадашки «Назначен» у РОЗ.
 */
export async function assignWithConfirm(
  requestId: string,
  itemIds: string[],
  assignedToId: string | null,
): Promise<BulkAssignResult | null> {
  const res = await bulkAssignProcurement(requestId, itemIds, assignedToId)
  if (!res.conflicts || res.conflicts.length === 0) return res
  const ok = await confirm({
    title: `У ${res.conflicts.length} поз. уже есть работа другого закупщика`,
    message: conflictsMessage(res.conflicts),
    confirmText: 'Всё равно назначить',
    danger: true,
  })
  if (!ok) return null
  return bulkAssignProcurement(requestId, itemIds, assignedToId, true)
}

/**
 * Хук массового назначения позиций закупщику (инструмент видимости в очереди
 * квот). Работает с тем же `selectedIds`-состоянием, что и удаление/матрица.
 * `assignedToId=null` — снять назначение («показать всем»). Назначаются только
 * позиции с матрицей Китай/Производители — про пропущенные показываем toast.
 */
export function useBulkAssignProcurement(
  requestId: string | undefined,
  gridRef: RefObject<AgGridReact | null>,
  onClearSelection: () => void,
) {
  const queryClient = useQueryClient()
  const [isApplying, setIsApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function apply(itemIds: string[], assignedToId: string | null) {
    if (!requestId || itemIds.length === 0 || isApplying) return
    setIsApplying(true)
    setError(null)
    try {
      // Живая работа других закупщиков — спрашиваем и повторяем с force.
      const res = await assignWithConfirm(requestId, itemIds, assignedToId)
      if (!res) return
      const { updated, skipped } = res
      onClearSelection()
      gridRef.current?.api.deselectAll()
      await queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      const action = assignedToId ? 'Назначено' : 'Назначение снято'
      if (skipped > 0) {
        toast.error(
          `${action}: ${updated}. Пропущено ${skipped} — назначать можно только позиции с матрицей «Китай»/«Производители».`,
        )
      } else if (updated > 0) {
        toast.success(`${action}: ${updated} ${updated === 1 ? 'позиция' : 'позиций'}`)
      } else {
        // Всё подходит по матрице, но менять нечего — назначение уже такое.
        toast.info('Изменений нет — выбранное назначение уже установлено у этих позиций')
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось назначить закупщика'
      setError(msg)
    } finally {
      setIsApplying(false)
    }
  }

  return { isApplying, error, apply, clearError: () => setError(null) }
}
